import React, { useState, useEffect } from "react";
import Sidebar from "../Components/Sidebar";
import Navbar from "../Components/Navbar";
import Footer from "../Components/Footer";
import {
  fetchAllowedMenus,
  fetchAccessPermissionData,
} from "../Services/APIService";
import CreateAcessPermission from "../Components/CreateAcessPermission";
import AccessPermissionView from "../Components/AcessPermissionView";
import UpdateAccessPermission from "../Components/UpdateAccessPermission";
import CollapsibleSidebar from "../Components/CollapsibleSidebar";
import { useLocation } from "react-router-dom";
import OverlayLoader from "../Components/OverlayLoader";
import { Toaster, toast } from "react-hot-toast";

interface MenuItem {
  menuID: number;
  menuName: string;
  menuUrl: string;
  icon: string;
  subMenuList?: [];
}

interface accessPermissions {
  permissionId: number;
  userTypeId: number;
  menuId: number;
  isAccessAllowed: boolean;
  menuName: string;
  parentMenuName: string;
  isMenu: boolean;
  isSubMenu: boolean;
  userTypeName: string;
}

const AccessPermissions: React.FC = () => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [allowedMenus, setAllowedMenus] = useState<MenuItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateCard, setShowCreateCard] = useState(false);
  const [showUpdateCard, setShowUpdateCard] = useState(false);
  const [selectedAccessPermission, setSelectedAccessPermission] =
    useState<accessPermissions | null>(null);
  const [accessPermissions, setAccessPermissions] = useState<
    accessPermissions[]
  >([]);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const location = useLocation();

  const toggleSidebar = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  // Function to toggle create form
  const toggleCreateForm = () => {
    setShowCreateCard(!showCreateCard);
    // Hide update form if it's open
    if (showUpdateCard) {
      setShowUpdateCard(false);
      setSelectedAccessPermission(null);
    }
  };

  // Function to handle edit button click from AccessPermissionView
  const handleEditAccessPermission = (accessPermission: accessPermissions) => {
    setSelectedAccessPermission(accessPermission);
    setShowUpdateCard(true);
    // Hide create form if it's open
    if (showCreateCard) {
      setShowCreateCard(false);
    }
    // Scroll to the update card
    setTimeout(() => {
      const updateCard = document.getElementById("updateGroupCard");
      if (updateCard) {
        updateCard.scrollIntoView({ behavior: "smooth" });
      }
    }, 100);
  };

  // Function to handle create completion
  const handleCreateComplete = () => {
    setShowCreateCard(false);
    refreshGroups();
  };

  // Function to handle create cancel
  const handleCreateCancel = () => {
    setShowCreateCard(false);
  };

  // Function to handle update completion
  const handleUpdateComplete = () => {
    setShowUpdateCard(false);
    setSelectedAccessPermission(null);
    refreshGroups();
    toast.success("Access Permission updated successfully!");
  };

  // Function to handle update cancel
  const handleUpdateCancel = () => {
    setShowUpdateCard(false);
    setSelectedAccessPermission(null);
  };

  // Function to refresh access permissions data
  const refreshGroups = () => {
    setRefreshTrigger((prev) => prev + 1);
  };

  // Fetch menu data
  useEffect(() => {
    const getMenus = async () => {
      setIsLoading(true);
      try {
        const menuID = location.state?.menuID;
        const previousMenuID = localStorage.getItem("activeMenuID");
        if (menuID !== previousMenuID || menuID === null) {
          localStorage.setItem("activeMenuID", menuID);
        } else {
          localStorage.removeItem("activeMenuID");
        }
      } catch (error) {
        console.error("Error fetching menus:", error);
      } finally {
        setIsLoading(false);
      }
    };

    getMenus();
  }, []);

  // Fetch access permissions data
  useEffect(() => {
    const getAccessPermissions = async () => {
      try {
        const fetchAccessPermission = await fetchAccessPermissionData();
        setAccessPermissions(fetchAccessPermission);
      } catch (error) {
        console.error("Error fetching access permissions:", error);
      }
    };

    getAccessPermissions();
  }, [refreshTrigger]);

  return (
    <div className="wrapper">
      <Toaster
        position="top-center"
        reverseOrder={false}
        containerStyle={{
          zIndex: 10000,
        }}
      />
      {!isSidebarCollapsed && <CollapsibleSidebar />}
      <div className="main">
        <Navbar onToggleSidebar={toggleSidebar} />
        <div className="main-content-div">
          <div className="main-header-div"></div>
          <main className="content mainContent">
            <div className="container-fluid p-0">
              <div className="row">
                <div className="col-12 col-lg-12">
                  {showCreateCard && (
                    <CreateAcessPermission
                      onCreateComplete={handleCreateComplete}
                      onCancel={handleCreateCancel}
                    />
                  )}

                  {showUpdateCard && (
                    <div id="updateGroupCard">
                      <UpdateAccessPermission
                        accessPermissionData={selectedAccessPermission}
                        onUpdateComplete={handleUpdateComplete}
                        onCancel={handleUpdateCancel}
                      />
                    </div>
                  )}
                </div>
                <div className="col-12 col-lg-12">
                  <AccessPermissionView
                    accessPermissionData={accessPermissions}
                    onEditAccessPermission={handleEditAccessPermission}
                    onAddClick={toggleCreateForm}
                    showAddButton={!showCreateCard && !showUpdateCard}
                  />
                </div>
              </div>
            </div>
          </main>
        </div>
        <Footer />
      </div>
      <OverlayLoader
        loading={isLoading}
        message="Loading Access Permissions Data"
        submessage="This might take a moment..."
      />
    </div>
  );
};

export default AccessPermissions;
